import { ChronicDisease } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { dateFormatter, Translate } from '@dreryk/react-common';
import { StyledFlex, TextView } from '@dreryk/react-components';

import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';

import { patientMedicalInformationTranslationKeys } from '../../translations';

import { entryRemovalVisitor } from './ChronicDiseaseDetails.utils';

const EMPTY_VALUE = '-';

type ChronicDiseaseDetailsProps = {
   readonly chronicDisease: ChronicDisease;
};

// eslint-disable-next-line max-lines-per-function
export const ChronicDiseaseDetails = (props: ChronicDiseaseDetailsProps): JSX.Element => {
   const { chronicDisease } = props;

   return (
      <>
         <StyledFlex gap={'24px'}>
            <TextView
               testId={'entryDate'}
               label={patientMedicalInformationTranslationKeys.chronicDisease.details.entryDate}
            >
               {chronicDisease.diagnosisCreationDate
                  ? dateFormatter(chronicDisease.diagnosisCreationDate)
                  : EMPTY_VALUE}
            </TextView>
            <TextView
               testId={'entryRemovalDate'}
               label={patientMedicalInformationTranslationKeys.chronicDisease.details.entryRemovalDate}
            >
               {chronicDisease.diagnosisRemovalDate ? dateFormatter(chronicDisease.diagnosisRemovalDate) : EMPTY_VALUE}
            </TextView>
         </StyledFlex>

         <TextView
            testId={'diagnosisCreatedPerson'}
            label={patientMedicalInformationTranslationKeys.chronicDisease.details.diagnosisCreatedPerson}
         >
            <PractitionerSafePresenter
               testId={'diagnosisCreatedPerson'}
               practitioner={{
                  displayName: chronicDisease.diagnosedByName,
                  medicalLicenceNumber: chronicDisease.diagnosedByLicenceNumber,
               }}
               type={'text'}
               withIdentification
            />
         </TextView>

         {chronicDisease.diagnosisRemovedByName ? (
            <TextView
               testId={'entryRemovalPerson'}
               label={patientMedicalInformationTranslationKeys.chronicDisease.details.entryRemovalPerson}
            >
               {entryRemovalVisitor(Boolean(chronicDisease.removedBy?.medicalLicenceNumber))({
                  visitDoctor: () => (
                     <PractitionerSafePresenter
                        testId={'diagnosisRemovedByPerson'}
                        practitioner={{
                           displayName: chronicDisease.diagnosisRemovedByName,
                           medicalLicenceNumber: chronicDisease.diagnosisRemovedByLicenceNumber,
                        }}
                        type={'text'}
                        withIdentification
                     />
                  ),
                  visitSpecialist: () => (
                     <>
                        {chronicDisease.removedBy ? (
                           <>
                              <PractitionerSafePresenter
                                 testId={'entryRemovedByPerson'}
                                 practitioner={chronicDisease.removedBy}
                                 type={'text'}
                                 withIdentification
                              />
                              {', '}
                           </>
                        ) : null}
                        <Translate>
                           {patientMedicalInformationTranslationKeys.chronicDisease.details.entryRemovalOnDoctorRequest}
                        </Translate>{' '}
                        <PractitionerSafePresenter
                           testId={'diagnosisRemovedByPerson'}
                           practitioner={{
                              displayName: chronicDisease.diagnosisRemovedByName,
                              medicalLicenceNumber: chronicDisease.diagnosisRemovedByLicenceNumber,
                           }}
                           type={'text'}
                           withIdentification
                        />
                     </>
                  ),
               })}
            </TextView>
         ) : null}

         {chronicDisease.removalReason ? (
            <TextView
               testId={'entryRemovalReason'}
               label={patientMedicalInformationTranslationKeys.chronicDisease.details.entryRemovalReason}
            >
               {chronicDisease.removalReason}
            </TextView>
         ) : null}

         {chronicDisease.diagnosis ? (
            <TextView
               testId={'diagnosis'}
               label={patientMedicalInformationTranslationKeys.chronicDisease.details.diagnosis}
            >
               {chronicDisease.diagnosis.getCode().asString()} {chronicDisease.diagnosis.getName().asString()}
            </TextView>
         ) : null}
      </>
   );
};
